import type { DeckSection, ParsedDeckLine } from "@/types/deckBuilder";

/**
 * Parse a TCGplayer-style Pokémon deck list into structured lines.
 *
 * Supported formats:
 *   - Flat: "4 Charmander 26"
 *   - With set code:  "4 Ultra Ball PAR 196" or "4 Charizard ex SVI 125"
 *   - With "x" separator: "4x Charmander" / "1 x Charizard ex"
 *   - Sectioned headers: "Pokémon (18)" / "Trainer (28)" / "Energy (14)"
 *   - Comments: lines starting with "//", "#", or blank lines are ignored.
 *
 * The parser is intentionally permissive — anything ambiguous is preserved in
 * `raw` so the matcher and UI can still surface it for the user to fix.
 */
export function parseDeckList(input: string): ParsedDeckLine[] {
  const out: ParsedDeckLine[] = [];
  if (!input) return out;

  let currentSection: DeckSection | undefined;

  const lines = input.split(/\r?\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith("//") || line.startsWith("#")) continue;

    const sectionHit = detectSectionHeader(line);
    if (sectionHit) {
      currentSection = sectionHit;
      continue;
    }

    const parsed = parseDeckListLine(line);
    if (!parsed) continue;
    parsed.section = currentSection ?? guessSection(parsed.name);
    out.push(parsed);
  }

  return out;
}

const SECTION_PATTERNS: { regex: RegExp; section: DeckSection }[] = [
  { regex: /^pok[eé]mon\b/i, section: "Pokemon" },
  { regex: /^trainer/i, section: "Trainer" },
  { regex: /^energy/i, section: "Energy" },
];

function detectSectionHeader(line: string): DeckSection | undefined {
  // Section headers don't start with a quantity (digit followed by a space).
  if (/^\d/.test(line)) return undefined;
  for (const { regex, section } of SECTION_PATTERNS) {
    if (regex.test(line)) return section;
  }
  return undefined;
}

/**
 * Best-effort section guess for cards parsed without an explicit section
 * header. Used as a last resort — the matcher will still try regardless.
 */
function guessSection(name: string): DeckSection {
  const n = name.toLowerCase();
  if (n.includes("energy")) return "Energy";
  return "Unknown";
}

const QTY_PREFIX = /^(\d+)\s*x?\s+/i;
const SET_CODE_AND_NUMBER = /\s+([A-Z]{2,5})\s+(\d+[a-z]?)$/;
const TRAILING_NUMBER = /\s+(\d+[a-z]?)$/;

function parseDeckListLine(line: string): ParsedDeckLine | null {
  const qtyMatch = line.match(QTY_PREFIX);
  if (!qtyMatch) return null;
  const quantity = parseInt(qtyMatch[1], 10);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  let remainder = line.slice(qtyMatch[0].length).trim();
  if (!remainder) return null;

  let setCode: string | undefined;
  let cardNumber: string | undefined;

  // Try "name SET 123" first.
  const setAndNum = remainder.match(SET_CODE_AND_NUMBER);
  if (setAndNum) {
    setCode = setAndNum[1];
    cardNumber = setAndNum[2];
    remainder = remainder.slice(0, setAndNum.index).trim();
  } else {
    // Fall back to "name 123".
    const trailing = remainder.match(TRAILING_NUMBER);
    if (trailing) {
      cardNumber = trailing[1];
      remainder = remainder.slice(0, trailing.index).trim();
    }
  }

  const name = remainder.replace(/\s+/g, " ").trim();
  if (!name) return null;

  return {
    quantity,
    name,
    setCode,
    cardNumber,
    raw: line,
  };
}
